/**
 * Tax Receipt Card Component
 *
 * Shows tax receipt status and download options
 */

import { FC } from 'react';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useDocumentDownload } from '@/hooks/useDocumentDownload';
import type { DashboardOverviewResponse } from '@/types/dashboard.types';

interface TaxReceiptCardProps {
  overviewData: DashboardOverviewResponse | null;
}

export const TaxReceiptCard: FC<TaxReceiptCardProps> = ({ overviewData }) => {
  const currentYear = new Date().getFullYear();
  const currentYearReceipt = overviewData?.tax_receipts.find((r) => r.year === currentYear);
  const { downloadPdf, openPdfInTab, isDownloading, error } = useDocumentDownload();

  const handleDownload = async (year: number) => {
    try {
      await downloadPdf('tax-receipt', year);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleView = async (year: number) => {
    try {
      await openPdfInTab('tax-receipt', year);
    } catch (err) {
      console.error('View failed:', err);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Tax Receipts</h3>
        <p className="text-sm text-gray-600">Download your donation receipts</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error.message}</p>
        </div>
      )}

      <div className="space-y-3">
        {/* Current Year */}
        <div className="border border-primary-200 rounded-lg p-4 bg-primary-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <DocumentTextIcon className="h-5 w-5 text-primary-600 mr-2" />
              <span className="font-semibold text-gray-900">{currentYear} Tax Receipt</span>
            </div>
            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
              In Progress
            </span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Year-to-date</p>
              <p className="text-xl font-bold text-gray-900">
                $
                {currentYearReceipt?.total_amount.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
            <button disabled className="text-sm text-gray-400 cursor-not-allowed flex items-center">
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              Available after Dec 31
            </button>
          </div>
        </div>

        {/* Previous Years */}
        {overviewData?.tax_receipts
          .filter((receipt) => receipt.year < currentYear)
          .slice(0, 2)
          .map((receipt) => (
            <div
              key={receipt.year}
              className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="font-semibold text-gray-900">{receipt.year} Tax Receipt</span>
                </div>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                  {receipt.status}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Total donations</p>
                  <p className="text-lg font-bold text-gray-900">
                    $
                    {receipt.total_amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleView(receipt.year)}
                    disabled={isDownloading}
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <button
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleDownload(receipt.year)}
                    disabled={isDownloading}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <button
          className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
          disabled={true}
        >
          View All Receipts →
        </button>
      </div>
    </div>
  );
};
